import React, { useState } from 'react';
import './QAChatMessage.css';

export interface QAConcern {
  id: string;
  severity: 'critical' | 'major' | 'minor' | 'suggestion';
  category: string;
  title: string;
  description: string;
  line_numbers?: number[];
  suggested_fix?: string;
  example_code?: string;
}

export interface QAReviewData {
  summary: string;
  concerns: QAConcern[];
  positive_aspects: string[];
  critical_count: number;
  major_count: number;
  minor_count: number;
  suggestion_count: number;
}

interface QAChatMessageProps {
  qaStatus: 'passed' | 'has_issues' | 'error';
  qaData?: QAReviewData;
  qaAnalysisText?: string;
  onAction: (action: 'accept' | 'revise' | 'discuss') => void;
  isProcessing?: boolean;
  isReadOnly?: boolean;  // When showing historical QA review
}

const QAChatMessage: React.FC<QAChatMessageProps> = ({
  qaStatus,
  qaData,
  qaAnalysisText,
  onAction,
  isProcessing = false,
  isReadOnly = false
}) => {
  const [expandedConcerns, setExpandedConcerns] = useState<Set<string>>(new Set());
  const [showFullAnalysis, setShowFullAnalysis] = useState(false);

  const toggleConcern = (concernId: string) => {
    const newExpanded = new Set(expandedConcerns);
    if (newExpanded.has(concernId)) {
      newExpanded.delete(concernId);
    } else {
      newExpanded.add(concernId);
    }
    setExpandedConcerns(newExpanded);
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return '🔴';
      case 'major': return '🟡';
      case 'minor': return '🔵';
      case 'suggestion': return '💡';
      default: return '•';
    }
  };

  const getSeverityLabel = (severity: string) => {
    return severity.charAt(0).toUpperCase() + severity.slice(1);
  };

  // If there's an error or no structured data, show the raw analysis
  if (qaStatus === 'error' || !qaData) {
    return (
      <div className="qa-chat-message">
        <div className="qa-header">
          <h3>📋 Quality Review Results</h3>
        </div>
        
        {qaAnalysisText && (
          <div className="qa-analysis-text">
            <div className="analysis-content">
              {qaAnalysisText}
            </div>
          </div>
        )}

        {qaStatus === 'error' && (
          <div className="qa-error-notice">
            ⚠️ Unable to extract structured findings. Please review the analysis above.
          </div>
        )}

        <div className="qa-actions">
          <button 
            className="action-btn primary"
            onClick={() => onAction('accept')}
            disabled={isProcessing}
          >
            ✅ Proceed to Execution
          </button>
          <button 
            className="action-btn secondary"
            onClick={() => onAction('discuss')}
            disabled={isProcessing}
          >
            💬 Discuss
          </button>
        </div>
      </div>
    );
  }

  // Render structured QA results
  const hasIssues = qaData.critical_count > 0 || qaData.major_count > 0;
  const hasAnyFeedback = qaData.concerns.length > 0;

  return (
    <div className="qa-chat-message">
      <div className="qa-header">
        <h3>📋 Quality Review Results</h3>
      </div>

      {/* Show success message if no issues */}
      {qaStatus === 'passed' && (
        <div className="qa-success-message">
          <div className="success-icon">✨</div>
          <div className="success-content">
            <h4>Excellent! Your script passed all quality checks.</h4>
            <p>The script is ready for execution.</p>
          </div>
        </div>
      )}

      {/* Show positive aspects if any */}
      {qaData.positive_aspects && qaData.positive_aspects.length > 0 && (
        <div className="qa-strengths">
          <h4>✅ Strengths</h4>
          <ul>
            {qaData.positive_aspects.map((aspect, idx) => (
              <li key={idx}>{aspect}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Show issues if any */}
      {hasAnyFeedback && (
        <div className="qa-issues">
          <h4>
            {hasIssues ? '⚠️ Issues Found' : '💡 Suggestions'}
          </h4>
          
          {/* Summary counts */}
          <div className="issue-summary">
            {qaData.critical_count > 0 && (
              <span className="count critical">🔴 {qaData.critical_count} Critical</span>
            )}
            {qaData.major_count > 0 && (
              <span className="count major">🟡 {qaData.major_count} Major</span>
            )}
            {qaData.minor_count > 0 && (
              <span className="count minor">🔵 {qaData.minor_count} Minor</span>
            )}
            {qaData.suggestion_count > 0 && (
              <span className="count suggestion">💡 {qaData.suggestion_count} Suggestions</span>
            )}
          </div>

          {/* Issue list */}
          <div className="issue-list">
            {qaData.concerns.map(concern => {
              const isExpanded = expandedConcerns.has(concern.id);
              return (
                <div key={concern.id} className={`issue-item ${concern.severity}`}>
                  <div 
                    className="issue-header"
                    onClick={() => toggleConcern(concern.id)}
                  >
                    <span className="issue-icon">{getSeverityIcon(concern.severity)}</span>
                    <span className="issue-title">{concern.title}</span>
                    <span className="issue-severity">({getSeverityLabel(concern.severity)})</span>
                    <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                  </div>
                  
                  {isExpanded && (
                    <div className="issue-details">
                      <p className="issue-description">{concern.description}</p>
                      
                      {concern.suggested_fix && (
                        <div className="suggested-fix">
                          <strong>Suggested Fix:</strong> {concern.suggested_fix}
                        </div>
                      )}
                      
                      {concern.line_numbers && concern.line_numbers.length > 0 && (
                        <div className="line-numbers">
                          <strong>Lines:</strong> {concern.line_numbers.join(', ')}
                        </div>
                      )}
                      
                      {concern.example_code && (
                        <div className="example-code">
                          <strong>Example:</strong>
                          <pre>{concern.example_code}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Option to show/hide full analysis text */}
      {qaAnalysisText && (
        <div className="qa-full-analysis-toggle">
          <button 
            className="toggle-btn"
            onClick={() => setShowFullAnalysis(!showFullAnalysis)}
          >
            {showFullAnalysis ? '▼ Hide' : '▶ Show'} Full Analysis
          </button>
          
          {showFullAnalysis && (
            <div className="qa-analysis-text">
              <div className="analysis-content">
                {qaAnalysisText}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action buttons - only show if not read-only */}
      {!isReadOnly && (
        <div className="qa-actions">
          <button 
            className={`action-btn ${hasIssues ? 'warning' : 'primary'}`}
            onClick={() => onAction('accept')}
            disabled={isProcessing}
          >
            {hasIssues ? '⚠️ Proceed Despite Issues' : '✅ Proceed to Execution'}
          </button>
          
          {hasIssues && (
            <button 
              className="action-btn secondary"
              onClick={() => onAction('revise')}
              disabled={isProcessing}
            >
              🔧 Fix Issues
            </button>
          )}
          
          <button 
            className="action-btn tertiary"
            onClick={() => onAction('discuss')}
            disabled={isProcessing}
          >
            💬 Discuss
          </button>
        </div>
      )}

      {isProcessing && (
        <div className="qa-processing">
          Processing your request...
        </div>
      )}
    </div>
  );
};

export default QAChatMessage;